<script setup lang="ts">
import { useReferencesStore } from "@/store/references";
import { useDefaultStore } from "@/store";

const router = useRouter();
const refStore = useReferencesStore();
const store = useDefaultStore();

export interface ISolarPowerPlant {
	id: number;
	path: string;
	blt_id: number;
	thumb: string;
	cover: string;
	titleTop: string;
	titleBottom?: string;
	location: string;
	solar_panels: string;
	inverters: string;
	system_power: string;
	batteries: string;
	solar_panels_number: number | null;
	expected_production?: number | null;
	description?: string;
	content: string;
	gallery?: string[];
	slug: string;
}

const filteredReferences = computed(() => {
	return (
		refStore.referencesList?.filter(
			(ref: ISolarPowerPlant) => ref.blt_id === 1,
		) || []
	);
});

function showCard(n: number) {
	store.activeCardLegal = store.activeCardLegal == n ? -1 : n;
}

function handleCardClick(event: { stopPropagation: () => void }) {
	event.stopPropagation();
}

async function toReference(ref: ISolarPowerPlant) {
	await (ref.path == "blog"
		? router.push({
				name: "LegalPostPage",
				params: { slug: ref.slug },
			})
		: router.push({
				name: "LegalReference",
				params: { slug: ref.slug },
			}));
}
</script>

<template lang="pug">
.interactiv-map-wrapper
	.view
		.map
			img(src="@/assets/image/interactiveMap/map.png", alt="map", loading="lazy")
			template(v-for="(ref, index) in filteredReferences", :key="index")
				.pin(
					:class="[store.activeCardLegal == ref.id ? 'pin-active' : '', 'pin' + ref.id]",
					@click.stop="showCard(ref.id)"
				)
					img.pin-item(
						src="@/assets/image/interactiveMap/pin.png",
						alt="pin",
						loading="lazy"
					)
					.hover-card(
						:class="[store.activeCardLegal == ref.id ? 'hover-card-active' : '', 'hover-card' + ref.id]",
						@click.stop="handleCardClick($event)"
					)
						.title {{ ref.titleTop }}
							.close(@click.stop="showCard(ref.id)") &#10005;
						.image
							img.thumb.thumb-contain(
								:src="ref.thumb",
								alt="thumbanail",
								loading="lazy"
							)
						.text
							|
							| {{ ref.description }}
						.text.see-more-wrapper
							.see-more(@click="toReference(ref)") {{ $t("app.seeMore") }}
</template>

<style lang="scss" scoped>
.interactiv-map-wrapper {
	display: flex;
	justify-content: center;

	.view {
		.map {
			position: relative;

			img {
				width: 100%;
			}

			.pin {
				position: absolute;
				display: none;

				.hover-card {
					display: none;
					flex-direction: column;
					position: absolute;
					justify-content: center;
					width: 300px;
					z-index: 1;
					opacity: 0;

					&.hover-card-active {
						display: flex;
						opacity: 1;
						animation: fade-in-out 1s ease-in-out forwards;
						.pin-item {
							filter: brightness(0) invert(1);
						}
					}

					.title {
						padding: 24px;
						background: $app-dark-color;
						color: $text-color-lighter;
						width: 100%;
						display: grid;
						place-items: center;
						border-radius: 10px 10px 0 0;
						text-transform: uppercase;
						font-family: Pelinka, sans-serif;
						font-size: 16px;
						line-height: 20px;
						font-weight: 700;
						text-align: center;

						.close {
							color: $white;
							position: absolute;
							top: 8px;
							right: 8px;
						}
					}

					.image {
						text-align: center;
						padding: 10px;
						background-color: rgb(255, 255, 255);
						display: flex;
						justify-content: center;
						.thumb {
							height: 100px;
							border-radius: 50%;
							width: fit-content;
							max-width: 100%;
							aspect-ratio: 1 / 1;
							object-fit: cover;

							&.thumb-contain {
								object-fit: contain;
							}
						}
					}

					.text {
						@include ellipsis;

						-webkit-line-clamp: 7;
						padding: 0 24px;
						background-color: $text-color-lighter;
						font-family: "Galano Grotesque Alt", sans-serif;
						font-size: 14px;
						line-height: 18px;
						font-weight: 300;
						text-align: justify;
						border-radius: 0;
						max-height: calc(7 * 18px);
						overflow: hidden;

						&.see-more-wrapper {
							padding-top: 0;
							display: flex;
							align-items: center;
							height: 60px;
							border-radius: 0 0 10px 10px;
						}

						.see-more {
							cursor: pointer;
							font-weight: 700;
							color: $app-accent-color1;
							text-transform: uppercase;

							&:hover {
								color: $app-accent-color1-lighter;
							}
						}
					}
				}
				.pin-item {
					width: 30px;
					height: 25px;
					cursor: pointer;
				}
				&.pin5 {
					left: 68%;
					top: 73%;
					display: block;
				}
				&.pin8 {
					left: 36%;
					top: 51%;
					display: block;
				}

				&.pin12 {
					left: 43%;
					top: 45%;
					display: block;
				}
				&.pin14 {
					left: 18%;
					top: 26%;
					display: block;
				}
				&.pin19 {
					left: 73%;
					top: 56%;
					display: block;
				}
				&.pin29 {
					left: 52%;
					top: 58%;
					display: block;
				}
				&.pin48 {
					left: 9%;
					top: 11%;
					display: block;
				}
				&.pin50 {
					left: 38%;
					top: 34%;
					display: block;
				}
				&.pin51 {
					left: 40%;
					top: 36%;
					display: block;
				}
				&.pin53 {
					left: 12%;
					top: 13%;
					display: block;
				}
				.hover-card5 {
					left: -213px;
					bottom: 40px;
				}
				.hover-card8 {
					left: -100px;
					bottom: 40px;
				}
				.hover-card12 {
					left: -122px;
					bottom: 38px;
				}
				.hover-card14 {
					left: -39px;
					top: 28px;
				}
				.hover-card19 {
					left: -228px;
					bottom: 40px;
				}
				.hover-card29 {
					left: -156px;
					bottom: 39px;
				}
				.hover-card48 {
					left: -9px;
					top: 29px;
				}
				.hover-card50 {
					left: -108px;
					bottom: 39px;
				}
				.hover-card51 {
					left: -116px;
					bottom: 39px;
				}
				.hover-card53 {
					left: -9px;
					top: 29px;
				}

				&.pin-active {
					cursor: pointer;

					.hover-card {
						display: flex;
					}

					.pin-item {
						filter: brightness(0) invert(1);
					}
				}
			}
		}
	}
}

@media screen and (width >= 760px) {
	.interactiv-map-wrapper {
		.view {
			.map {
				.pin {
					.hover-card {
						width: 393px;

						.text {
							font-size: 20px;
							line-height: 27px;
							max-height: calc(7 * 27px);
						}

						.title {
							font-size: 22px;
							line-height: 25.3px;

							.close {
								display: none;
							}
						}

						.image {
							.thumb {
								height: 126px;
							}
						}
					}
					.pin-item {
						width: unset;
						height: unset;
					}
					.hover-card5 {
						left: -332px;
						bottom: 65px;
						top: unset;
					}
					.hover-card8 {
						left: 0;
						bottom: 72px;
						top: unset;
					}
					.hover-card12 {
						left: 0;
						bottom: 68px;
						top: unset;
					}
					.hover-card14 {
						left: 0;
						top: 55px;
						bottom: unset;
					}
					.hover-card18 {
						left: -400px;
						bottom: 11px;
						top: unset;
					}
					.hover-card29 {
						left: -335px;
						bottom: 68px;
						top: unset;
					}
					.hover-card48 {
						left: 0;
						bottom: 68px;
						top: unset;
					}
					.hover-card50 {
						left: 60px;
						top: -17px;
						bottom: unset;
					}
					.hover-card51 {
						left: -6px;
						top: 45px;
						bottom: unset;
					}
					.hover-card53 {
						left: 60px;
						top: -17px;
						bottom: unset;
					}
				}
			}
		}
	}
}

@media screen and (width >= 1660px) {
	.interactiv-map-wrapper {
		.view {
			.map {
				.pin {
					&:hover {
						.hover-card {
							display: none;
						}
					}
					/* stylelint-disable-next-line no-descending-specificity */
					.hover-card {
						width: 393px;
						opacity: 0;

						.text {
							border-radius: 0;
						}

						&.hover-card-active {
							display: flex;
							opacity: 1;
							animation: fade-in-out 1s ease-in-out forwards;
							.pin-item {
								filter: brightness(0) invert(1);
							}
						}
					}
					.hover-card5 {
						left: 6vw;
						bottom: 220%;
						top: unset;
					}
					.hover-card8 {
						left: 18.3vw;
						bottom: -83px;
						top: unset;
					}
					.hover-card12 {
						left: 15.7vw;
						bottom: -141px;
						top: unset;
					}
					.hover-card14 {
						left: 25vw;
						top: -90px;
						bottom: unset;
					}
					.hover-card19 {
						left: 4.3vw;
						bottom: -64px;
						top: unset;
					}
					.hover-card29 {
						left: 12.3vw;
						bottom: 29px;
						top: unset;
					}
					.hover-card48 {
						left: 843%;
						top: 72px;
						bottom: unset;
					}
					.hover-card50 {
						left: 18.3vw;
						bottom: -270px;
						top: unset;
					}
					.hover-card51 {
						left: 17.3vw;
						bottom: -270px;
						top: unset;
					}
					.hover-card53 {
						left: 819%;
						top: 72px;
						bottom: unset;
					}
				}
			}
		}
	}
}

@media screen and (width >= 1900px) {
	.interactiv-map-wrapper {
		.view {
			.map {
				.pin {
					&:hover {
						.hover-card {
							display: none;
						}
					}
					/* stylelint-disable-next-line no-descending-specificity */
					.hover-card {
						width: 393px;
						opacity: 0;

						.text {
							border-radius: 0;
						}

						&.hover-card-active {
							display: flex;
							opacity: 1;
							animation: fade-in-out 1s ease-in-out forwards;
							.pin-item {
								filter: brightness(0) invert(1);
							}
						}
					}
					.hover-card5 {
						left: 259%;
						bottom: 177%;
						top: unset;
					}
					.hover-card8 {
						left: 610%;
						bottom: -105px;
						top: unset;
					}
					.hover-card12 {
						left: 538%;
						top: -249px;
						bottom: unset;
					}
					.hover-card14 {
						left: 810%;
						bottom: -333px;
						top: unset;
					}
					.hover-card19 {
						left: 207%;
						bottom: -71px;
						top: unset;
					}
					.hover-card29 {
						left: 443%;
						top: -390px;
						bottom: unset;
					}
					.hover-card48 {
						left: 906%;
						bottom: -509px;
						top: unset;
					}
					.hover-card50 {
						left: 594%;
						bottom: -291px;
						top: unset;
					}
					.hover-card51 {
						left: 572%;
						bottom: -272px;
						top: unset;
					}
					.hover-card53 {
						left: 906%;
						bottom: -509px;
						top: unset;
					}
				}
			}
		}
	}
}

@keyframes fade-in-out {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}
</style>
